import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Container,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import TableContainer from "../../Components/Common/TableContainer";
import Loader from "../../Components/Common/Loader";
import SearchableSelect from "../../Components/Common/SearchableSelect";

import {
  adminGetAllPatrol,
  adminGetComboMember,
  adminGetComboPatrol,
} from "helpers/backend_helpers";
import { formatDate } from "helpers/globalFunction";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import WorkActivitiesDetail from "./WorkActivitiesDetail";
import DownloadModalWorkActivities from "./DownloadModalWorkActivities";

const WorkActivities = () => {
  const menu: any = sessionStorage.getItem("menu");
  const [tableData, setTableData] = useState<any>([]);
  const [data, setData] = useState<any>({});
  const [sort, setSort] = useState("createTime");
  const [dir, setDir] = useState(-1);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);

  const [detailModal, setDetailModal] = useState<boolean>(false);
  const [downloadModal, setDownloadModal] = useState<boolean>(false);

  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filterDate, setFilterDate] = useState<any>([]);
  const [filterCategory, setFilterCategory] = useState<any>(null);
  const [filterMember, setFilterMember] = useState<any>(null);
  const [filterLocation, setFilterLocation] = useState(null);
  const [flatpickrKey, setFlatpickrKey] = useState(0);

  const getData = () => {
    setLoading(true);
    let query: any = {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sort: sort,
      dir: dir,
    };

    if (filterDate[0] && filterDate[1]) {
      query.startDate = moment(filterDate[0]).format("YYYY-MM-DD");
      query.endDate = moment(filterDate[1]).format("YYYY-MM-DD");
    }
    if (filterMember) {
      query.userId = filterMember.value;
    }
    if (filterCategory) {
      query.memberCategory = filterCategory.value;
    }

    adminGetAllPatrol(query)
      .then((response: any) => {
        setTableData(
          response.content.map((el: any, index: any) => {
            const { createTime } = el;
            return {
              ...el,
              index: index + pagination.pageIndex * pagination.pageSize + 1,
              tanggal: formatDate(createTime, "fullDateTime") || "-",
            };
          })
        );
        setRowCount(response.totalElements);
        setTotalPages(response.totalPages);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const searchMembers = async (name: string) => {
    const query = { name };
    return await adminGetComboMember(query);
  };

  const formatMemberOption = (member: any) => {
    return {
      label: `${member.name} - ${member.noId}`,
      value: member._id,
    };
  };

  const searchLocation = async (name: string) => {
    const query = { name };
    return await adminGetComboPatrol(query);
  };

  const formatLocationOption = (location: any) => {
    return {
      label: `${location.name}`,
      value: location._id,
    };
  };

  const handleResetFilter = () => {
    setFilterDate([]);
    setFilterCategory(null);
    setFilterMember(null);
    setFilterLocation(null);
    setFlatpickrKey((prev) => prev + 1);
  };

  const handleToggleFilter = () => {
    if (showFilter) {
      handleResetFilter();
    }
    setShowFilter(!showFilter);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: "#",
        accessorKey: "index",
        enableColumnFilter: false,
        enableSorting: false,
        size: 50,
      },
      {
        header: "Tanggal & Jam",
        accessorKey: "tanggal",
        enableColumnFilter: false,
        enableSorting: false,
        size: 150,
      },
      {
        header: "Nama Karyawan",
        accessorKey: "member.name",
        enableColumnFilter: false,
        enableSorting: false,
        size: 150,
      },
      {
        header: "No Reg",
        accessorKey: "member.noId",
        enableColumnFilter: false,
        enableSorting: false,
        size: 150,
      },
      {
        header: "Kategori",
        accessorKey: "member.category",
        enableColumnFilter: false,
        enableSorting: false,
        size: 150,
      },
      {
        header: "Aksi",
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
        cell: (cellProps: any) => {
          return (
            <div className="hstack gap-2">
              <UncontrolledTooltip
                target={`detail-${cellProps.row.original._id}`}
              >
                Detail
              </UncontrolledTooltip>
              <button
                id={`detail-${cellProps.row.original._id}`}
                className="btn btn-sm btn-soft-info"
                onClick={() => {
                  setDetailModal(!detailModal);
                  setData(cellProps.row.original);
                }}
              >
                <i className="ri-eye-line" />
              </button>
            </div>
          );
        },
      },
    ];
    if (menu.includes("PATROL_LOCATION")) {
      return [
        ...baseColumns.slice(0, 5),
        {
          accessorKey: "nameCheckPoint",
          header: "Lokasi",
          enableColumnFilter: false,
          enableSorting: false,
          size: 150,
        },
        ...baseColumns.slice(5),
      ];
    }
    return baseColumns;
  }, []);

  const comboCategory = [
    { value: "EMPLOYEE", label: "EMPLOYEE" },
    { value: "SECURITY", label: "SECURITY" },
    { value: "CLEANING_SERVICE", label: "CLEANING SERVICE" },
    { value: "OFFICE_BOY", label: "OFFICE BOY" },
    { value: "DRIVER", label: "DRIVER" },
    { value: "GARDENER", label: "GARDENER" },
  ];

  useEffect(() => {
    getData();
  }, [
    pagination,
    sort,
    dir,
    filterDate,
    filterMember,
    filterCategory,
    filterLocation,
  ]);

  document.title = "Data Aktivitas Pekerjaan";

  return (
    <React.Fragment>
      <WorkActivitiesDetail
        show={detailModal}
        onCloseClick={() => setDetailModal(!detailModal)}
        data={data}
      />
      <DownloadModalWorkActivities
        show={downloadModal}
        onCloseClick={() => setDownloadModal(!downloadModal)}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Data Aktivitas Pekerjaan"
            pageTitle="Aktivitas Pekerjaan"
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <div className="d-flex align-items-center">
                    <h5 className="card-title mb-0 flex-grow-1">
                      Data Aktivitas Pekerjaan
                    </h5>
                    <div className="flex-shrink-0">
                      <div className="d-flex gap-2 flex-wrap">
                        <button
                          className="btn btn-soft-primary btn-sm"
                          onClick={handleToggleFilter}
                        >
                          <i className="ri-filter-3-line align-bottom me-1"></i>
                          Filters
                        </button>
                        <button
                          className="btn btn-soft-dark btn-sm"
                          onClick={() => setDownloadModal(!downloadModal)}
                        >
                          <i className="ri-file-download-line align-bottom me-1"></i>
                          Unduh Data Aktivitas Pekerjaan
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="pt-0">
                  <Collapse isOpen={showFilter}>
                    <Row className="g-3 mb-4">
                      <Col lg={3}>
                        <div>
                          <Flatpickr
                            key={flatpickrKey}
                            className="form-control"
                            options={{
                              mode: "range",
                              dateFormat: "d M Y",
                            }}
                            onChange={(e) => {
                              setFilterDate(e);
                            }}
                            value={filterDate}
                            placeholder="Pilih Tanggal"
                          />
                        </div>
                      </Col>
                      <Col lg={3}>
                        <Select
                          value={filterCategory}
                          onChange={(e: any) => {
                            setFilterCategory(e);
                          }}
                          options={comboCategory}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          placeholder="Pilih Kategori Karyawan"
                          isClearable
                        />
                      </Col>
                      <Col lg={3}>
                        <SearchableSelect
                          value={filterMember}
                          onChange={setFilterMember}
                          onSearch={searchMembers}
                          formatOptionLabel={formatMemberOption}
                          placeholder="Cari Karyawan"
                          minSearchLength={2}
                          itemsPerPage={5}
                          isClearable
                        />
                      </Col>
                      {menu.includes("PATROL_LOCATION") && (
                        <Col lg={3}>
                          <SearchableSelect
                            value={filterLocation}
                            onChange={setFilterLocation}
                            onSearch={searchLocation}
                            formatOptionLabel={formatLocationOption}
                            placeholder="Cari Lokasi"
                            minSearchLength={2}
                            itemsPerPage={5}
                            isClearable
                          />
                        </Col>
                      )}
                      <Col lg={12}>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-soft-secondary btn-sm"
                            onClick={handleResetFilter}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            Reset
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </Collapse>
                  <div>
                    {loading ? (
                      <Loader />
                    ) : (
                      <TableContainer
                        columns={columns}
                        data={tableData || []}
                        customPageSize={10}
                        theadClass="text-muted text-uppercase"
                        manualPagination
                        pagination={pagination}
                        pageCount={totalPages}
                        onPaginationChange={setPagination}
                        divClass="table-responsive"
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default WorkActivities;
